"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { RootState } from "@store/store";
import {
  createCustomerProfile,
} from "@store/features/missingCustomerSlice";
import { MissingCustomerSchema } from "../auth/MissingCustomerSchema";
import { format } from "date-fns";
import {

  Switch as KeepSwitch,
  Spinner,
} from "keep-react";
import {
  selectBrandId,
  selectBrandString,
  selectPrivacyLink,
  selectTermsLink,
  selectLoyaltyLink,
} from "@store/features/styleSchemaSlice";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";
import clsx from "clsx";
import { CountrySelect } from "@/components/ui/CountrySelect";
import { BirthdayInput } from "@/components/ui/BirthdayInput";
import { getCountryByCode } from "@/lib/constants/countries";
import DOMPurify from 'dompurify';

interface MissingCustomerFormData {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  telefon?: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  dateOfBirth: Date;
  marketingAccepted: boolean;
}

export const MissingCustomerForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const brandId = useSelector(selectBrandId);
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;

  const { isModalOpen, isSubmitting } = useSelector(
    (state: RootState) => state.missingCustomer
  );

  const [userEmail, setUserEmail] = React.useState<string>("");
  const brandString = useSelector(selectBrandString);
  const privacyUrl = useSelector(selectPrivacyLink);
  const termsUrl = useSelector(selectTermsLink);
  const loyaltyUrl = useSelector(selectLoyaltyLink);

  const schema = React.useMemo(() => MissingCustomerSchema(t), [t]);

  // Formular initialisieren
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    trigger,
    getValues
  } = useForm<MissingCustomerFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      salutation: t("missingCustomer.form.mr"),
      country: "DE",
      marketingAccepted: true,
      email: userEmail || "",
    },
    mode: "onChange",
  });
  
  // E-Mail aus localStorage lesen und in das Formular eintragen
  React.useEffect(() => {
    const storedEmail = localStorage.getItem("mail") || "";
    if (storedEmail) {
      setUserEmail(storedEmail);
      setValue("email", storedEmail);
    }
  }, [setValue]);

  // Wir führen eine zusätzliche Validierung durch, wenn der Benutzer das Formular abschickt
  React.useEffect(() => {
    // Bei erster Initialisierung validieren
    const validateForm = async () => {
      await trigger();
    };
    validateForm();
  }, [trigger]);

  // Formular absenden Funktion
  const handleFormSubmit = async () => {
    // Formular manuell validieren
    const isValid = await trigger();
    
    if (!isValid) {
      console.error('Formular enthält Fehler');
      return;
    }
    
    const formData = getValues();
    console.log('Formulardaten:', formData);
    
    try {
      // Transformiere Country-Code zu deutschem Namen
      const countryName = getCountryByCode(formData.country)?.nameDE || formData.country;
      
      const result = await dispatch(
        createCustomerProfile({
          email: formData.email,
          anrede: formData.salutation === t("missingCustomer.form.mr") ? "1" : "2",
          vorname: formData.firstName,
          nachname: formData.lastName,
          telefon: formData.telefon || "",
          strasse: formData.street,
          hausnummer: formData.houseNumber,
          adresszusatz: "",
          plz: formData.postalCode,
          ort: formData.city,
          country: countryName,
          geburtsdatum: formData.dateOfBirth
            ? format(formData.dateOfBirth, "dd.MM.yyyy")
            : "",
          // Werte für die Bonuskarte
          marketing: formData.marketingAccepted ? 1 : 0,
          trigger_create_card: true,
          brand_create_card: brandId ? brandId.toString() : "",
        })
      );
      
      console.log('Ergebnis der API-Anfrage:', result);
      
      // Nach erfolgreichem Request Seite neu laden
      if (result.meta.requestStatus === "fulfilled") {
        console.log('Formular erfolgreich abgesendet, lade Seite neu...');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error("Fehler beim Erstellen des Kundenprofils:", error);
    }
  };
  
  // Formular-Submission-Handler (für das form onSubmit-Event)
  const onSubmit = (data: MissingCustomerFormData) => {
    console.log('Formular wurde abgesendet:', data);
    handleFormSubmit();
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={() => {}} modal>
      <DialogContent className="bg-white md:max-w-3xl max-h-[90vh] overflow-y-auto mx-auto w-[90%] rounded-lg p-4 [&>button]:hidden">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-xl font-semibold">
            {t("missingCustomer.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("missingCustomer.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 px-6 py-4 w-full"
          >
            {/* Persönliche Informationen */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Anrede */}
                <div>
                  <Label htmlFor="salutation">
                    {t("missingCustomer.form.salutation")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="salutation"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue
                            placeholder={t("missingCustomer.form.salutation")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={t("missingCustomer.form.mr")}>
                            {t("missingCustomer.form.mr")}
                          </SelectItem>
                          <SelectItem value={t("missingCustomer.form.mrs")}>
                            {t("missingCustomer.form.mrs")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {/* Vorname */}
                <div>
                  <Label htmlFor="firstName">
                    {t("missingCustomer.form.firstName")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="firstName"
                        {...field}
                        className={`mt-1 ${
                          errors.firstName ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.firstName")}
                      />
                    )}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                {/* Nachname */}
                <div>
                  <Label htmlFor="lastName">
                    {t("missingCustomer.form.lastName")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="lastName"
                        {...field}
                        className={`mt-1 ${
                          errors.lastName ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.lastName")}
                      />
                    )}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Geburtsdatum */}
                <div>
                  <BirthdayInput
                    name="dateOfBirth"
                    control={control}
                    label={t("missingCustomer.form.dateOfBirth")}
                    required={true}
                    error={errors.dateOfBirth?.message}
                  />
                </div>

                {/* Telefon */}
                <div>
                  <Label htmlFor="telefon">
                    {t("missingCustomer.form.telefon")}
                  </Label>
                  <Controller
                    name="telefon"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="telefon"
                        type="tel"
                        {...field}
                        className="mt-1"
                        placeholder={t("register.placeholders.telefon")}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Adressinformationen */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Straße */}
                <div>
                  <Label htmlFor="street">
                    {t("missingCustomer.form.street")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="street"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="street"
                        {...field}
                        className={`mt-1 ${
                          errors.street ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.street")}
                      />
                    )}
                  />
                  {errors.street && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.street.message}
                    </p>
                  )}
                </div>

                {/* Hausnummer */}
                <div>
                  <Label htmlFor="houseNumber">
                    {t("missingCustomer.form.houseNumber")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="houseNumber"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="houseNumber"
                        {...field}
                        className={`mt-1 ${
                          errors.houseNumber ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.houseNumber")}
                      />
                    )}
                  />
                  {errors.houseNumber && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.houseNumber.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* PLZ */}
                <div className="col-span-1">
                  <Label htmlFor="postalCode">
                    {t("missingCustomer.form.postalCode")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="postalCode"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="postalCode"
                        {...field}
                        className={`mt-1 ${
                          errors.postalCode ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.postalCode")}
                      />
                    )}
                  />
                  {errors.postalCode && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.postalCode.message}
                    </p>
                  )}
                </div>

                {/* Ort */}
                <div className="col-span-3">
                  <Label htmlFor="city">
                    {t("missingCustomer.form.city")}
                    <span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="city"
                        {...field}
                        className={`mt-1 ${
                          errors.city ? "border-red-500" : ""
                        }`}
                        placeholder={t("register.placeholders.city")}
                      />
                    )}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.city.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <CountrySelect
                  name="country"
                  control={control}
                  label={t("missingCustomer.form.country")}
                  placeholder={t("register.placeholders.country")}
                  required={true}
                  defaultValue="DE"
                  error={errors.country?.message}
                />
              </div>

              {/* E-Mail */}
              <div>
                <Label htmlFor="email">
                  {t("register.labels.email")}
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => {
                    // Direkt während des Renderns die E-Mail aus dem localStorage holen
                    const emailFromLocalStorage = typeof window !== 'undefined' ? localStorage.getItem('mail') : '';
                    
                    return (
                      <Input
                        id="email"
                        {...field}
                        value={emailFromLocalStorage || field.value}
                        onChange={field.onChange}
                        disabled={!!emailFromLocalStorage}
                        className="mt-1 bg-gray-100"
                        placeholder={t("register.placeholders.email")}
                      />
                    );
                  }}
                />
              </div>
            </div>

            {/* Marketing und Datenschutz */}
            <div className="space-y-4">
              <div className="space-y-3 mt-2">
                {/* Marketing-Einwilligung */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <Controller
                      name="marketingAccepted"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <KeepSwitch
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                            }}
                            className="bg-primary"
                          />
                        </div>
                      )}
                    />
                    <p
                      className="flex-1 text-sm font-medium text-gray-700"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(t("register.marketing.label", {
                          privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${loyaltyUrl}" target="_blank">`,
                          "/privacyLink": "</a>",
                        })),
                      }}
                    />
                  </div>
                </div>

                {/* Datenschutz und AGB */}
                <div className="flex flex-col">
                  <div className="flex items-center gap-4">
                    <div>
                      <KeepSwitch checked={true} className="bg-primary" />
                    </div>
                    <div className="flex items-start">
                      <span className="text-red-500">*</span>
                      <p
                        className="flex-1 text-sm font-medium text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(t("register.terms.label", {
                            termsLink: `<a class="text-[var(--accent-color)] hover:underline" href="${termsUrl}" target="_blank">`,
                            "/termsLink": "</a>",
                            privacyLink: `<a class="text-[var(--accent-color)] hover:underline" href="${privacyUrl}" target="_blank">`,
                            "/privacyLink": "</a>",
                            brandString,
                          })),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="w-full flex justify-center mt-8 gap-4">
              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={isSubmitting || !isValid}
                className={clsx(
                  "px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed",
                  {
                    "flex items-center gap-2": isSubmitting,
                  }
                )}
                style={{
                  background: buttonColor,
                  color: "white",
                }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner color="primary" />
                    <span className="mr-2">
                      {t("missingCustomer.submittingButton")}
                    </span>
                  </>
                ) : (
                  t("missingCustomer.submitButton")
                )}
              </button>
            </div>
            <p className="text-sm text-slate-600">{t("app.requiredFields")}</p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
